import { arrayMove } from "@dnd-kit/sortable";
import { useCreateTimeslotMutation } from "api/mutations/timeslots";
import { useGetForumByIdQuery } from "api/queries/forums";
import Button from "components/styleguide/components/Button";
import Divider from "components/styleguide/components/Divider";
import Form from "components/styleguide/components/Form";
import Input from "components/styleguide/components/Input";
import Select from "components/styleguide/components/Select";
import Typography from "components/styleguide/components/Typography";
import TimeType from "enums/TimeType";
import { toNumber } from "lodash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";

import SessionTable from "../EditTimeslot/SessionsTable";
import {
  applyFormValuesToSessions as applySessionFormValuesToSessions,
  applySequentialOrder,
  createDefaultSession,
  createSessionWithDefaultDuration,
  getDuplicateSessionTimePeriods,
  getDuplicateTimePeriods,
  getDuplicateTimeSlotSessionIds,
  getDuplicateTimeSlotValidationMessage,
  getExistingDuplicateTimeSlotSessionIds,
  getSessionTimePeriods,
  handleTimeslotError,
  isDuplicateSessionError,
  isDuplicateTimeSlotError,
  normalizeSessionOrder,
  showDuplicateSessionTimeValidationError,
  showDuplicateTimeSlotValidationError,
  showTimeslotValidationError,
} from "../helpers";
import useUnsavedChangesPrompt from "../useUnsavedChangesPrompt";

const { Option } = Select;

const NewTimeSlot = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [sessionForm] = Form.useForm();
  const [timeslotForm] = Form.useForm();
  const { id } = useOutletContext();

  const [formLayout] = useState("inline");
  const [forum, setForum] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [disableOrdering, setDisableOrdering] = useState(false);
  const [isTimeslotDirty, setIsTimeslotDirty] = useState(false);
  const [isSessionDirty, setIsSessionDirty] = useState(false);
  const [defaultSessionDuration, setDefaultSessionDuration] = useState(0);
  const [duplicateSessionIds, setDuplicateSessionIds] = useState([]);
  const [duplicateTimePeriods, setDuplicateTimePeriods] = useState([]);
  const [duplicateMessage, setDuplicateMessage] = useState(undefined);
  const [hasDuplicateTimeslotError, setHasDuplicateTimeslotError] = useState(false);

  const createTimeslotMutation = useCreateTimeslotMutation();
  const isCreateTimeslotLoading = createTimeslotMutation.isLoading;

  const isDirty = isTimeslotDirty || isSessionDirty;
  useUnsavedChangesPrompt(isDirty, isCreateTimeslotLoading, t("unsavedChangesPrompts.timeSlotCreate"));

  const handleError = (response) => handleTimeslotError(response, t);

  const applyFormValuesToSessions = (sourceSessions) => {
    return applySessionFormValuesToSessions(sessionForm, sourceSessions);
  };

  const getSessionsFromForm = async () => {
    await sessionForm.validateFields();
    return applyFormValuesToSessions(sessions);
  };

  const showValidationError = () => showTimeslotValidationError(t);

  const showDuplicateTimeslotFieldsError = () => {
    setHasDuplicateTimeslotError(true);
    showDuplicateTimeSlotValidationError(t);
  };

  const markSessionChanged = () => {
    setDuplicateSessionIds([]);
    setDuplicateTimePeriods([]);
    setDuplicateMessage(undefined);
    setIsSessionDirty(true);
  };

  const markTimeslotChanged = () => {
    setDuplicateSessionIds([]);
    setDuplicateTimePeriods([]);
    setDuplicateMessage(undefined);
    setHasDuplicateTimeslotError(false);
    setIsTimeslotDirty(true);
  };

  const mapSessionsToRequest = (sourceSessions) =>
    sourceSessions.map((session) => ({
      startTime: session.startTime,
      endTime: session.endTime,
      order: session.order,
    }));

  const handleSubmit = async (values) => {
    let sessionsToSave;

    try {
      sessionsToSave = await getSessionsFromForm();
      const duplicatePeriodsInForm = getDuplicateSessionTimePeriods(sessionsToSave);

      if (duplicatePeriodsInForm.length) {
        setDuplicateSessionIds([]);
        setDuplicateTimePeriods(duplicatePeriodsInForm);
        setDuplicateMessage(String(t("Duplicate session time")));
        showDuplicateSessionTimeValidationError(t);
        return;
      }

      await createTimeslotMutation.mutateAsync({
        forumId: toNumber(id),
        timeSlotRequestModel: {
          ...values,
          disableOrdering,
          sessions: mapSessionsToRequest(sessionsToSave),
        },
      });

      toast.success(t("Timeslot and sessions created"));
      setDuplicateSessionIds([]);
      setDuplicateTimePeriods([]);
      setDuplicateMessage(undefined);
      setHasDuplicateTimeslotError(false);
      setIsTimeslotDirty(false);
      setIsSessionDirty(false);
      setTimeout(() => navigate("../"));
    } catch (e) {
      if (e?.errorFields) {
        showValidationError();
        return;
      }

      if (isDuplicateTimeSlotError(e?.response)) {
        showDuplicateTimeslotFieldsError();
        return;
      } else if (isDuplicateSessionError(e?.response)) {
        const sessionIds = getDuplicateTimeSlotSessionIds(e?.response);
        const existingSessionIds = getExistingDuplicateTimeSlotSessionIds(e?.response);
        const timePeriods = getDuplicateTimePeriods(e?.response);

        setDuplicateSessionIds(sessionIds);
        setDuplicateMessage(
          existingSessionIds.length ? String(t("Session time already exists")) : String(t("Duplicate session time")),
        );
        setDuplicateTimePeriods(timePeriods.length ? timePeriods : getSessionTimePeriods(sessionsToSave));
      }

      handleError(e?.response);
    }
  };

  const handleSessionAdd = () => {
    const currentSessions = applyFormValuesToSessions(sessions);
    const newSession = createSessionWithDefaultDuration({
      forum,
      order: currentSessions.length + 1,
      defaultSessionDuration,
    });

    setSessions(applySequentialOrder([...currentSessions, newSession]));
    markSessionChanged();
  };

  const handleSessionDelete = (session) => {
    setDuplicateSessionIds([]);
    setDuplicateTimePeriods([]);
    setDuplicateMessage(undefined);
    const currentSessions = applyFormValuesToSessions(sessions);
    const newData = currentSessions.filter((item) => item.order !== session.order);
    if (newData.length === 0 && forum) {
      const defaultSession = createDefaultSession(forum);
      setSessions([defaultSession]);
      markSessionChanged();
      return;
    }

    setSessions(applySequentialOrder(newData));
    markSessionChanged();
  };

  const timeTypes = Object.keys(TimeType)
    .filter((key) => key !== "toDisplayName")
    .map((key) => {
      return (
        <Option value={TimeType[key]} key={key}>
          {t(`timeTypes.${TimeType.toDisplayName(TimeType[key])}`)}
        </Option>
      );
    });

  const { isLoading } = useGetForumByIdQuery(
    { forumId: toNumber(id) },
    {
      onSuccess: ({ data }) => {
        setForum(data);
        if (sessions.length === 0) {
          const defaultSession = createDefaultSession(data);
          setSessions(normalizeSessionOrder([defaultSession]));
        }
      },
      onError: ({ response }) => handleError(response),
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const handleSessionReorder = (event) => {
    if (!event.over || event.active.id === event.over.id) {
      return;
    }

    const currentSessions = applyFormValuesToSessions(sessions);
    const orderedSessions = normalizeSessionOrder(currentSessions);
    const oldIndex = orderedSessions.findIndex((item) => String(item.order) === String(event.active.id));
    const newIndex = orderedSessions.findIndex((item) => String(item.order) === String(event.over.id));

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    setSessions(applySequentialOrder(arrayMove(orderedSessions, oldIndex, newIndex)));
    markSessionChanged();
  };

  return (
    <div>
      <Form
        onFinish={handleSubmit}
        onFinishFailed={showValidationError}
        onValuesChange={markTimeslotChanged}
        id="new-timeslot-form"
        layout={formLayout}
        form={timeslotForm}
        style={{ width: "100%" }}
      >
        <Form.Item
          label={t("description")}
          name="description"
          validateStatus={hasDuplicateTimeslotError ? "error" : undefined}
          rules={[
            {
              required: true,
              message: t("errors.required", {
                prop: "$t(timeslotDescription)",
              }),
            },
          ]}
        >
          <Input style={{ width: 200 }} />
        </Form.Item>
        <Form.Item
          label={t("timeType")}
          name="timeSlotType"
          validateStatus={hasDuplicateTimeslotError ? "error" : undefined}
          rules={[
            {
              required: true,
              message: t("errors.required", { prop: "$t(timeType)" }),
            },
          ]}
        >
          <Select style={{ width: 200 }}>{timeTypes}</Select>
        </Form.Item>
        <Form.Item style={{ marginLeft: "auto" }}>
          <Button htmlType="submit" type="primary" loading={isCreateTimeslotLoading} disabled={isCreateTimeslotLoading}>
            {t("save")}
          </Button>
        </Form.Item>
        {hasDuplicateTimeslotError && (
          <div style={{ width: "100%", marginTop: 4 }}>
            <Typography.Text type="danger" role="alert">
              {getDuplicateTimeSlotValidationMessage(t)}
            </Typography.Text>
          </div>
        )}
      </Form>
      <Divider />
      <div className="section">
        <SessionTable
          form={sessionForm}
          data={sessions}
          onAdd={handleSessionAdd}
          onDelete={handleSessionDelete}
          onDragEnd={handleSessionReorder}
          setDisableOrdering={setDisableOrdering}
          disableOrdering={disableOrdering}
          loading={isLoading}
          isSaveLoading={isCreateTimeslotLoading}
          title={t("Sessions")}
          description={t("Add at least 1 session to your Timeslot")}
          defaultSessionDuration={defaultSessionDuration}
          onDefaultSessionDurationChange={(value) => setDefaultSessionDuration(value || 0)}
          onSessionValuesChange={markSessionChanged}
          duplicateSessionIds={duplicateSessionIds}
          duplicateTimePeriods={duplicateTimePeriods}
          duplicateMessage={duplicateMessage}
        />
      </div>
    </div>
  );
};

export default NewTimeSlot;
